import React from 'react';
import { Box } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

export type SafetyReportIconSemantic =
  | 'safetyActionRequired'
  | 'evidenceReview'
  | 'unsafeConditionAssessment'
  | 'evidencePackagePrepared'
  | 'signedOffReport'
  | 'blockedBeforeSignoff'
  | 'categorySummary'
  | 'notClaimedOrLimitation';

type SafetyReportIconSpec = {
  ariaLabel: string;
  bg: string;
  color: string;
  Icon: SvgIconComponent;
};

export const SAFETY_REPORT_ICON_SPECS: Record<SafetyReportIconSemantic, SafetyReportIconSpec> = {
  safetyActionRequired: {
    ariaLabel: 'Safety action required',
    bg: 'rgba(245, 158, 11, 0.18)',
    color: '#d97706',
    Icon: WarningAmberRoundedIcon,
  },
  evidenceReview: {
    ariaLabel: 'Evidence review',
    bg: 'rgba(245, 158, 11, 0.14)',
    color: '#d97706',
    Icon: FactCheckOutlinedIcon,
  },
  unsafeConditionAssessment: {
    ariaLabel: 'Risk pattern assessment awaiting review',
    bg: 'rgba(37, 99, 235, 0.12)',
    color: '#2563eb',
    Icon: SearchRoundedIcon,
  },
  evidencePackagePrepared: {
    ariaLabel: 'Package available',
    bg: 'rgba(13, 148, 136, 0.14)',
    color: '#0f766e',
    Icon: Inventory2OutlinedIcon,
  },
  signedOffReport: {
    ariaLabel: 'Signed Safety Scan',
    bg: 'rgba(34, 197, 94, 0.14)',
    color: '#15803d',
    Icon: CheckCircleOutlineRoundedIcon,
  },
  blockedBeforeSignoff: {
    ariaLabel: 'Blocked before sign-off',
    bg: 'rgba(245, 158, 11, 0.14)',
    color: '#d97706',
    Icon: LockOutlinedIcon,
  },
  categorySummary: {
    ariaLabel: 'Hazard category summary',
    bg: 'rgba(147, 51, 234, 0.14)',
    color: '#7e22ce',
    Icon: BarChartRoundedIcon,
  },
  notClaimedOrLimitation: {
    ariaLabel: 'Limitation or not claimed',
    bg: 'rgba(148, 163, 184, 0.14)',
    color: '#64748b',
    Icon: InfoOutlinedIcon,
  },
};

type SafetyReportSemanticIconProps = {
  semantic: SafetyReportIconSemantic;
  size?: 'summary' | 'hero' | 'section' | 'inline';
  decorative?: boolean;
};

const SIZE_PRESETS = {
  summary: { box: 40, icon: 20, circle: true },
  hero: { box: 44, icon: 24, circle: true },
  section: { box: 22, icon: 22, circle: false },
  inline: { box: 16, icon: 16, circle: false },
} as const;

export function SafetyReportSemanticIcon({
  semantic,
  size = 'summary',
  decorative = true,
}: SafetyReportSemanticIconProps): React.ReactElement {
  const spec = SAFETY_REPORT_ICON_SPECS[semantic];
  const preset = SIZE_PRESETS[size];

  if (!preset.circle) {
    return (
      <spec.Icon
        sx={{ fontSize: preset.icon, color: spec.color }}
        aria-hidden={decorative ? true : undefined}
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : spec.ariaLabel}
      />
    );
  }

  return (
    <Box
      sx={{
        width: preset.box,
        height: preset.box,
        borderRadius: '50%',
        bgcolor: spec.bg,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : spec.ariaLabel}
    >
      <spec.Icon
        sx={{ fontSize: preset.icon, color: spec.color }}
        aria-hidden={decorative ? true : undefined}
      />
    </Box>
  );
}
